import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea, PageProps } from '../index'
import { pageComponent } from '../../higher-order-components/pageComponent'
import Header from './Header'
import { useTaskActualWorkApprovalStatusData } from './hooks/TaskActualWorkApproveStatusData'
import { TaskActualWorkApprovalStatusGridOptions } from './gridOptions'
import DateVO from '../../../vo/DateVO'
import { usePageState } from './hooks/pageState'
import Loading from '../../components/process-state-notifications/Loading'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useExcel } from './hooks/excel'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import MultiSelectDialog from '../../components/dialogs/MultiSelectDialog'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { intl } from '../../../i18n'
import {
  useBulkSheetState,
  RestoreType,
} from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import _ from 'lodash'
import { useSearchCondition } from './hooks/useSearchCondition'

import { useDialog } from '../ProjectTaskActualWorkApprovalStatus/hooks/dialog'
import { fetchCount } from './taskActualWorkApprovalStatus'
import { setData } from '../../../store/singleSheet'
import { DecimalAndSexagesimalScale } from '../../../lib/functions/decimalAndSexagesimalScale'

type Props = PageProps

const TaskActualWorkApprovalStatus = (props: Props) => {
  const functionUuid = props.uuid
  const ref = useRef<HTMLDivElement>(null)
  const { data, loading, fetchRecords } = useTaskActualWorkApprovalStatusData()
  const pageState = usePageState({ functionUuid })
  const gridOptions = useMemo(() => {
    return TaskActualWorkApprovalStatusGridOptions(pageState.decimalScaleUnit)
  }, [pageState.decimalScaleUnit])
  const { initialized, displayDateTerm, handleMonthChange } = pageState

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [userCount, setUserCount] = useState<number>(0)
  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const [isStateRestored, setIsStateRestored] = useState<boolean>(false)
  const restoreTypes = useMemo<RestoreType[]>(
    () => ['COLUMN_STATE', 'FILTER_STATE'],
    []
  )

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    'TaskActualWorkApprovalStatus',
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    restoreTypes
  )

  const { dialogOpen, openDialog, closeDialog, onExportExcel } = useExcel(
    gridApiRef.current
  )
  const columnSetting = useColumnSetting()
  const dialogState = useDialog()
  const { searchCondition, onChange: onSearchConditionChange } =
    useSearchCondition()

  const handleSearch = useCallback(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm,
    fetchRecords,
    searchCondition,
    pageState.endDate,
  ])

  const countUsers = useCallback(async () => {
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()!
    const endDate = pageState.endDate.formatForApi()!
    const response = await fetchCount(startDate, endDate, searchCondition)
    setUserCount(response.json)
  }, [displayDateTerm, searchCondition, pageState.endDate])

  useEffect(() => {
    countUsers()
  }, [countUsers])

  useEffect(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    setData([])
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm,
    fetchRecords,
    pageState.endDate,
    searchCondition,
  ])

  useEffect(() => {
    if (!pageState.initialized || pageState.isCurrentMonth) return
    const lastDayOfMonth = new DateVO(displayDateTerm.endDate)
    if (pageState.endDate.formatForApi() !== lastDayOfMonth.formatForApi()) {
      pageState.setEndDate(lastDayOfMonth)
    }
  }, [
    pageState.initialized,
    pageState.isCurrentMonth,
    displayDateTerm,
    pageState,
  ])

  useEffect(() => {
    if (data.length > 0 && gridApiRef.current) {
      setTimeout(() => {
        gridApiRef.current?.refreshCells({
          columns: ['rowNumber'],
          force: true,
        })
      }, 100)
    }
  }, [data])

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  useEffect(() => {
    if (
      bulkSheetState.initialized &&
      bulkSheetState.restored &&
      !isStateRestored
    ) {
      if (
        bulkSheetState.filterState &&
        Object.keys(bulkSheetState.filterState).length > 0
      ) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }

      if (bulkSheetState.columnState.length > 0) {
        const sortedColumnsState: SortedColumnState[] =
          bulkSheetState.columnState
            .filter(col => col.sort)
            .map(col => {
              const colDef = (gridOptions.columnDefs as ColDef[])?.find(
                def => def.colId === col.colId || def.field === col.colId
              )
              return {
                colId: col.colId,
                field: colDef?.field,
                headerName: colDef?.headerName,
                sort: col.sort!,
              }
            })
        setSortColumnsState(sortedColumnsState)
      }
    }
  }, [
    bulkSheetState.initialized,
    bulkSheetState.restored,
    bulkSheetState.filterState,
    bulkSheetState.columnState,
    gridOptions.columnDefs,
    isStateRestored,
  ])

  const refresh = useCallback(() => {
    if (!initialized) return
    const startDate = new DateVO(displayDateTerm.startDate).formatForApi()
    const endDate = pageState.endDate.formatForApi()
    if (startDate && endDate) {
      fetchRecords(startDate, endDate, searchCondition)
    }
  }, [
    initialized,
    displayDateTerm.startDate,
    pageState.endDate,
    fetchRecords,
    searchCondition,
  ])

  const onDeleteFilteredColumn = useCallback((column: ColDef) => {
    if (!column || !gridApiRef.current) return
    const filterModel = gridApiRef.current.getFilterModel()
    delete filterModel[column.colId || column.field || '']
    gridApiRef.current.setFilterModel(filterModel)
  }, [])

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) return
    gridApiRef.current.setFilterModel(null)
  }, [])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterState = e.api.getFilterModel()
      bulkSheetState.saveFilterState(filterState)
      setFilteredColumns(
        Object.keys(filterState)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v)
      )
    },
    [bulkSheetState]
  )

  const onSortedColumnsStateChanged = useCallback((e: SortChangedEvent) => {
    const sortedColumns = e.api
      .getColumnState()
      .filter(col => !!col.sort)
      .map(col => {
        return {
          ...e.api.getColumnDef(col.colId),
          colId: col.colId,
        }
      })
      .filter(v => !!v) as ColDef[]

    const columnState = gridApiRef.current?.getColumnState()
    const sortedColumnState: { [colId: string]: ColumnState } = {}
    columnState &&
      columnState.forEach(state => {
        if (state.sort) {
          sortedColumnState[state.colId] = state
        }
      })

    const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
      return {
        colId: col.colId,
        field: col.field,
        headerName: col.headerName,
        sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
      }
    })

    setSortColumnsState(sortedColumnsState)
  }, [])

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      const columnState = gridApiRef.current?.getColumnState()
      columnState && bulkSheetState.saveColumnState(columnState)
      onSortedColumnsStateChanged(e)
    },
    [bulkSheetState, onSortedColumnsStateChanged]
  )

  const onGridReady = useCallback(
    params => {
      gridApiRef.current = params.api
      setIsGridReady(true)

      if (!_.isEmpty(bulkSheetState.columnState)) {
        params.api?.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
      setIsStateRestored(true)
    },
    [bulkSheetState.columnState, gridOptions]
  )

  const onFirstDataRendered = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridApiRef.current?.setFilterModel(bulkSheetState.filterState ?? null)
      if (bulkSheetState.filterState) {
        const filterKeys = Object.keys(bulkSheetState.filterState)
        const filtered =
          (gridOptions.columnDefs as ColDef[])?.filter(col =>
            filterKeys.includes(col.colId || col.field || '')
          ) || []
        setFilteredColumns(filtered)
      }
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState, gridOptions.columnDefs])

  const rememberColumnState = useCallback(() => {
    const columnState = gridApiRef.current?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState])

  const onDeleteSortedColumn = useCallback((colId: string | ColDef<any>) => {
    if (!gridApiRef.current) return

    gridApiRef.current.applyColumnState({
      state: [{ colId: colId.toString(), sort: null }],
    })
  }, [])

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridApiRef.current) return

    gridApiRef.current.applyColumnState({
      defaultState: { sort: null },
    })
  }, [])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridApiRef.current) return

      gridApiRef.current.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    []
  )

  const getCurrentUIState = useCallback(() => {
    return {
      columnState: bulkSheetState.columnState,
      filterState: bulkSheetState.filterState,
    }
  }, [bulkSheetState.columnState, bulkSheetState.filterState])

  const applyUIState = useCallback(
    (uiState: any) => {
      if (uiState.columnState && gridApiRef.current) {
        gridApiRef.current.applyColumnState({
          state: uiState.columnState,
          applyOrder: true,
        })
        bulkSheetState.saveColumnState(uiState.columnState)
      }
      if (uiState.filterState && gridApiRef.current) {
        gridApiRef.current.setFilterModel(uiState.filterState ?? null)
        bulkSheetState.saveFilterState(uiState.filterState)
      }
    },
    [bulkSheetState]
  )

  if (!initialized || !bulkSheetState.initialized || !pageState.initialized) {
    return (
      <PageArea>
        <Loading isLoading={true} />
      </PageArea>
    )
  }

  return (
    <PageArea>
      <Header
        loading={loading}
        refresh={refresh}
        onMonthChange={handleMonthChange}
        currentYear={
          displayDateTerm
            ? new DateVO(displayDateTerm.startDate).getYear()
            : undefined
        }
        currentMonth={
          displayDateTerm
            ? new DateVO(displayDateTerm.startDate).getMonth()
            : undefined
        }
        searchCondition={searchCondition}
        onSearchConditionChange={onSearchConditionChange}
        onSearch={handleSearch}
        recordCount={data?.length}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        sortColumnsState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        onClickExport={openDialog}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={(value: number) => {
          pageState.setRowHeight(value)
        }}
        userCount={userCount}
        decimalScaleUnit={pageState.decimalScaleUnit}
        onChangeDecimalScaleUnit={(unit: DecimalAndSexagesimalScale) => {
          pageState.setDecimalScaleUnit(unit)
        }}
        endDate={pageState.endDate}
        onEndDateChange={(date: DateVO) => {
          pageState.setEndDate(date)
        }}
        isCurrentMonth={pageState.isCurrentMonth}
        endDatePreset={pageState.endDatePreset}
        setEndDatePreset={pageState.setEndDatePreset}
      />
      <BulkSheetView
        ref={ref}
        rowData={data}
        {...gridOptions}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        onColumnMoved={rememberColumnState}
        onColumnResized={rememberColumnState}
        onColumnVisible={rememberColumnState}
        rowHeight={pageState.rowHeight}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        gridApi={gridApiRef.current}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={dialogState.openUiState}
        initializeColumnState={() => {
          if (!gridApiRef.current) return
          gridApiRef.current.resetColumnState()
          gridApiRef.current.setFilterModel(null)
          bulkSheetState.saveColumnState([])
          bulkSheetState.saveFilterState({})
          setFilteredColumns([])
          setSortColumnsState([])
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={90}
      />
      <SavedUIStateDialog
        open={dialogState.uiState}
        onClose={dialogState.closeUiState}
        onSelect={selectedUIState => {
          const uiStateToApply = selectedUIState.UIState || selectedUIState
          applyUIState(uiStateToApply)
          dialogState.closeUiState()
        }}
        applicationFunctionUuid={props.uuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        title={intl.formatMessage({
          id: 'bulksheet.toolbar.columnQuickFilter.label.restore',
        })}
        sharable={false}
        currentUIState={getCurrentUIState()}
      />
      {dialogOpen && (
        <MultiSelectDialog
          onClose={closeDialog}
          onSubmit={(allList, selectedItem) => {
            const selectedColIds = selectedItem.map(col => col.value)
            onExportExcel(selectedColIds)
          }}
          dialogTitle={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.title',
          })}
          submitButtonTitle={intl.formatMessage({
            id: 'dialog.exceloutput.submit',
          })}
          allCheckBoxLabel={intl.formatMessage({
            id: 'dialog.exceloutput.columnselect.allcheckboxlabel',
          })}
          getSelectList={() => {
            return (gridApiRef.current?.getColumns() || [])
              .filter(
                column =>
                  !['uuid', 'drag', 'action', 'select', 'rowNumber'].includes(
                    column.getColId()
                  )
              )
              .map(column => {
                return {
                  value: column.getColId(),
                  displayName:
                    gridApiRef.current?.getDisplayNameForColumn(column, null) ??
                    '',
                  defaultChecked:
                    column.isVisible() ||
                    column.getColDef().cellRendererParams?.uiMeta?.tree,
                }
              })
          }}
          hideIcon={true}
        />
      )}
      <Loading isLoading={loading} elem={ref.current} />
    </PageArea>
  )
}

export default pageComponent(TaskActualWorkApprovalStatus)
